import React, { useEffect, useMemo, useState } from "react";
import { Search, BookOpen, Sparkles } from "lucide-react";
import PageShell from "../components/PageShell";
import { fetchDreams } from "../toolsApi";

export default function Suenos() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("Todas");
  const [data, setData] = useState({ items: [], categories: ["Todas"] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const r = await fetchDreams(q, cat);
        if (active) setData(r);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [q, cat]);

  const grouped = useMemo(() => {
    const g = {};
    (data.items || []).forEach((d) => {
      g[d.category] = g[d.category] || [];
      g[d.category].push(d);
    });
    return g;
  }, [data]);

  return (
    <PageShell
      eyebrow={<><BookOpen className="w-3.5 h-3.5" /> Diccionario de Sueños</>}
      title="Significado de tus Sueños"
      subtitle="Cada sueño tiene un número en la quiniela dominicana. Busca lo que soñaste anoche y descubre los números asociados que han funcionado por generaciones."
      accent="rose"
    >
      {/* Buscador y filtros */}
      <div className="bg-white rounded-3xl shadow-xl border border-rose-100 p-6 sm:p-8 mb-8">
        <div className="flex items-center bg-rose-50/60 border-2 border-rose-100 focus-within:border-rose-500 rounded-full px-4 py-3 shadow-sm">
          <Search className="w-5 h-5 text-rose-500 mr-2" />
          <input
            data-testid="dreams-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="¿Qué soñaste? Ej: perro, agua, dinero..."
            className="flex-1 bg-transparent outline-none text-base text-gray-800"
          />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {data.categories.map((c) => (
            <button
              key={c}
              data-testid={`dreams-cat-${c}`}
              onClick={() => setCat(c)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition border ${
                cat === c
                  ? "bg-gradient-to-r from-rose-500 to-pink-500 text-white border-transparent shadow"
                  : "bg-white text-rose-600 border-rose-200 hover:bg-rose-50"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Resultados */}
      {loading && data.items.length === 0 ? (
        <div className="text-center py-12 text-gray-500">Cargando sueños...</div>
      ) : data.items.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">No encontramos sueños para "{q}".</p>
        </div>
      ) : (
        <div className="space-y-10">
          {Object.entries(grouped).map(([category, items]) => (
            <div key={category}>
              <h3 className="text-sm font-bold uppercase tracking-widest text-rose-600 mb-4 flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> {category}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((d) => (
                  <div
                    key={d.name}
                    data-testid={`dream-card-${d.name}`}
                    className="bg-white rounded-2xl border border-rose-100 shadow-sm p-5 hover:shadow-lg transition"
                  >
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <h4 className="font-extrabold text-[#0e4a7a]">{d.name}</h4>
                      <span
                        className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 text-white font-extrabold text-lg shadow-md"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        {d.number}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed mb-3">{d.tip}</p>
                    {d.alt?.length > 0 && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wider text-gray-400">Acompañan:</span>
                        {d.alt.map((a) => (
                          <span
                            key={a}
                            className="inline-flex items-center justify-center min-w-[34px] h-7 px-2 rounded-md bg-rose-50 text-rose-600 font-bold text-xs border border-rose-200"
                          >
                            {a}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </PageShell>
  );
}
